import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '@/lib/api-functions/api-auth';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useToast } from '@/hooks/use-toast';
import {
  fetchFavorites,
  getCachedFavorites,
  FavoriteItem,
  favoritesStore,
} from '@/lib/stores/favoritesStore';

interface FavoritesDataContextType {
  favorites: FavoriteItem[];
  isLoading: boolean;
  error: string | null;
  refreshFavorites: () => Promise<void>;
}

const FavoritesDataContext = createContext<FavoritesDataContextType | null>(null);

// Separate component that only renders when authenticated and has a selected game
const AuthenticatedFavoritesDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuthStore();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();
  const [isInitialized, setIsInitialized] = useState(false);
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!user?.id || !selectedGame?.id) return;

    setIsLoading(true);
    setError(null);
    try {
      // fetchFavorites now returns cached data immediately if available
      const fetchedFavorites = await fetchFavorites(user.id, selectedGame.id);
      setFavorites(fetchedFavorites);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load favorites";
      setError(message);
      console.error("FavoritesDataProvider - Error loading favorites:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, selectedGame?.id]);

  // Load cached data immediately when game changes, then fetch fresh data
  useEffect(() => {
    if (!user?.id || !selectedGame?.id) return;

    // Load cached data immediately for instant response
    const cachedFavorites = getCachedFavorites(user.id, selectedGame.id);
    if (cachedFavorites.length > 0) {
      setFavorites(cachedFavorites);
    }
    
    // Always fetch fresh data (which will be fast if cached, or do background sync)
    if (!isInitialized) {
      loadData();
      setIsInitialized(true);
    }
  }, [user?.id, selectedGame?.id, isInitialized, loadData]);

  // Reset initialization when selected game changes
  useEffect(() => {
    setIsInitialized(false);
  }, [user?.id, selectedGame?.id]);

  // Subscribe to store changes for real-time updates
  useEffect(() => {
    if (!user?.id || !selectedGame?.id) return;

    const unsubscribe = favoritesStore.subscribe(() => {
      // Get the latest data from the store and filter by current user and game
      const allFavorites = favoritesStore.getData();
      const userGameFavorites = allFavorites.filter(
        (fav) => fav.user_id === user.id && fav.game_id === selectedGame.id
      );
      setFavorites(userGameFavorites);
    });

    return () => unsubscribe();
  }, [user?.id, selectedGame?.id]);

  const value: FavoritesDataContextType = {
    favorites,
    isLoading,
    error,
    refreshFavorites: loadData,
  };

  return (
    <FavoritesDataContext.Provider value={value}>
      {children}
    </FavoritesDataContext.Provider>
  );
};

interface FavoritesDataProviderProps {
  children: React.ReactNode;
}

export const FavoritesDataProvider: React.FC<FavoritesDataProviderProps> = ({ children }) => {
  const { user } = useAuthStore();

  // If user is not authenticated, provide a minimal context
  if (!user) {
    const emptyValue: FavoritesDataContextType = {
      favorites: [],
      isLoading: false,
      error: null,
      refreshFavorites: async () => {},
    };

    return (
      <FavoritesDataContext.Provider value={emptyValue}>
        {children}
      </FavoritesDataContext.Provider>
    );
  }

  // If authenticated, use the real provider with data fetching
  return (
    <AuthenticatedFavoritesDataProvider>
      {children}
    </AuthenticatedFavoritesDataProvider>
  );
};

export function useFavoritesDataContext() {
  const context = useContext(FavoritesDataContext);
  if (!context) {
    throw new Error('useFavoritesDataContext must be used within a FavoritesDataProvider');
  }
  return context;
}

